<template>
    <div class="box has-no-glow-effect p-4 mb-3">
        <div class="columns is-vcentered mb-0">
            <div class="column is-3">
                <p class="is-size-7 has-text-grey-light">{{ translate("page.tech_account.username") }}</p>
                <h2 class="title is-6 mb-0 has-text-white">{{ account.username }}</h2>
            </div>

            <div class="column is-3">
                <p class="is-size-7 has-text-grey-light">{{ translate("form.password") }}</p>
                <input v-if="plainPassword !== undefined" class="input is-small" readonly :value="plainPassword" />
                <p v-else class="has-text-grey">{{ translate("page.tech_account.password_hidden") }}</p>
            </div>

            <div class="column">
                <p class="is-size-7 has-text-grey-light">{{ translate("page.tech_account.access_mode") }}</p>
                <p class="has-text-white">{{ modeLabel(account.mode) }}</p>
                <div v-if="account.mode === TECH_ACCOUNT_MODE_BUNDLED" class="tags mt-1">
                    <span v-for="bundle in selectedBundleOptions" :key="bundle.key" class="tag is-info is-light">
                        {{ bundle.label }}
                    </span>
                </div>
            </div>

            <div class="column is-narrow">
                <div class="buttons">
                    <button v-if="!editing" class="button is-small" @click="startEdit">
                        <span class="icon">
                            <i class="fas fa-pen"></i>
                        </span>
                        <span>{{ translate("common.edit") }}</span>
                    </button>
                    <LoadingButton :is-loading="deletingInProgress" class="button is-small is-danger" @click="deleteTechAccount">
                        <span class="icon">
                            <i class="fa fa-trash" />
                        </span>
                        <span>{{ translate("common.delete") }}</span>
                    </LoadingButton>
                </div>
            </div>
        </div>

        <template v-if="editing">
            <hr class="my-3" />

            <TechAccountAccessFields v-model:mode="editMode" v-model:bundles="editBundles" :radio-name="radioName" />

            <div class="buttons is-right mb-0">
                <button class="button is-small is-danger" @click="cancelEdit">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton
                    :is-loading="updatingAccessModeInProgress"
                    class="button is-small is-success"
                    :disabled="!canSaveAccessMode"
                    @click="saveAccessMode"
                >
                    <span class="icon">
                        <i class="fa fa-check" />
                    </span>
                    <span>{{ translate("common.save") }}</span>
                </LoadingButton>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import TechAccountAccessFields from "./TechAccountAccessFields.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import techAccount, { TechAccount } from "../Api/techAccount";
    import { TECH_ACCOUNT_MODE_FULL_ACCESS, TECH_ACCOUNT_MODE_BUNDLED, bundlesToOptions, Bundle, TechAccountMode } from "../Common/consts";

    const props = defineProps<{
        account: TechAccount;
        eventId: number;
        // Only set for an account created during this page visit: the password is not stored anywhere after that.
        plainPassword?: string;
    }>();

    const emit = defineEmits<{
        updated: [account: TechAccount];
        deleted: [id: string];
    }>();

    const editing = ref(false);
    const editMode = ref<TechAccountMode>(TECH_ACCOUNT_MODE_FULL_ACCESS);
    const editBundles = ref<Bundle[]>([]);
    const deletingInProgress = ref(false);
    const updatingAccessModeInProgress = ref(false);

    const radioName = computed(() => `techAccountMode-${props.account.id}`);
    const selectedBundleOptions = computed(() => bundlesToOptions(props.account.bundles));
    const canSaveAccessMode = computed(() => editMode.value !== TECH_ACCOUNT_MODE_BUNDLED || editBundles.value.length > 0);

    function modeLabel(currentMode: TechAccountMode): string {
        return currentMode === TECH_ACCOUNT_MODE_BUNDLED
            ? translate("page.tech_account.bundled")
            : translate("page.tech_account.full_access");
    }

    function startEdit(): void {
        editing.value = true;
        editMode.value = props.account.mode;
        editBundles.value = selectedBundleOptions.value.map(option => option.key);
    }

    function cancelEdit(): void {
        editing.value = false;
    }

    function saveAccessMode() {
        updatingAccessModeInProgress.value = true;
        return techAccount
            .updateAccessMode(props.eventId, props.account.id, editMode.value, editBundles.value)
            .then(account => {
                editing.value = false;
                emit("updated", account);
                successToast(translate("page.tech_account.access_mode_updated"));
            })
            .catch(handleError)
            .finally(() => {
                updatingAccessModeInProgress.value = false;
            });
    }

    function deleteTechAccount(): void | Promise<void> {
        if (!confirm(translate("page.tech_account.sure_to_delete"))) {
            return;
        }

        deletingInProgress.value = true;
        return techAccount
            .deleteTechAccount(props.eventId, props.account.id)
            .then(() => {
                emit("deleted", props.account.id);
            })
            .catch(handleError)
            .finally(() => {
                deletingInProgress.value = false;
            });
    }
</script>
